import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Workflow, Zap, Database, Shield } from 'lucide-react';
import { FloatingNodes } from './FloatingNodes';
export function HeroSection() {
  return <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-gradient-to-b from-sky-50/50 to-white" data-id="element-110">
      <FloatingNodes count={20} className="z-0" data-id="element-111" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full py-12 lg:py-20" data-id="element-112">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-113">
          {/* Text Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="text-center lg:text-left" data-id="element-114">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold uppercase tracking-wide mb-6" data-id="element-115">
              <span className="relative flex h-2 w-2" data-id="element-116">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75" data-id="element-117"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-500" data-id="element-118"></span>
              </span>
              Nimbusquest v2.0 is live
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6" data-id="element-119">
              Orchestrate Your <br className="hidden lg:block" data-id="element-120" />
              <span className="text-sky-500 relative inline-block" data-id="element-121">
                Workflows
                <svg className="absolute w-full h-3 -bottom-1 left-0 text-sky-200 -z-10" viewBox="0 0 100 10" preserveAspectRatio="none" data-id="element-122">
                  <path d="M0 5 Q 50 10 100 5" stroke="currentColor" strokeWidth="8" fill="none" data-id="element-123" />
                </svg>
              </span>
              , Effortlessly.
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed" data-id="element-124">
              Nimbusquest automates repetitive tasks, connects your tools, and
              gives your team superpowers — all from one elegant cloud platform.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4" data-id="element-125">
              <a href="#cta" className="w-full sm:w-auto px-8 py-4 bg-sky-500 text-white rounded-full font-semibold hover:bg-sky-600 transition-all shadow-lg hover:shadow-sky-200 flex items-center justify-center gap-2 group" data-id="element-126">
                Start Free Trial
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-127" />
              </a>
              <button className="w-full sm:w-auto px-8 py-4 bg-white text-slate-700 border border-slate-200 rounded-full font-semibold hover:border-sky-300 hover:text-sky-600 transition-all flex items-center justify-center gap-2" data-id="element-128">
                <Play size={18} className="fill-current" data-id="element-129" />
                Watch Demo
              </button>
            </div>

            <div className="mt-10 flex items-center justify-center lg:justify-start gap-6 text-slate-400 text-sm" data-id="element-130">
              <span className="flex items-center gap-2" data-id="element-131">
                <Shield size={16} data-id="element-132" /> SOC2 Compliant
              </span>
              <span className="flex items-center gap-2" data-id="element-133">
                <Zap size={16} data-id="element-134" /> 99.99% Uptime
              </span>
            </div>
          </motion.div>

          {/* Visual Content - Workflow Diagram */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative lg:h-[500px] flex items-center justify-center" data-id="element-135">
            <div className="relative w-full max-w-md aspect-square" data-id="element-136">
              {/* Central Hub */}
              <motion.div animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-white rounded-2xl shadow-xl border border-sky-100 flex flex-col items-center justify-center z-20" data-id="element-137">
                <div className="w-12 h-12 bg-sky-500 rounded-xl flex items-center justify-center text-white mb-2" data-id="element-138">
                  <Workflow size={24} data-id="element-139" />
                </div>
                <span className="text-xs font-bold text-slate-700" data-id="element-140">
                  Core Flow
                </span>
                <span className="text-[10px] text-slate-400" data-id="element-141">Running...</span>

                {/* Connection dots */}
                <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-sky-400 rounded-full" data-id="element-142"></div>
                <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-sky-400 rounded-full" data-id="element-143"></div>
                <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-sky-400 rounded-full" data-id="element-144"></div>
                <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-sky-400 rounded-full" data-id="element-145"></div>
              </motion.div>

              {/* Satellite Nodes */}
              {[{
              icon: Database,
              label: 'Data Sync',
              pos: 'top-0 left-1/2 -translate-x-1/2',
              delay: 0
            }, {
              icon: Zap,
              label: 'Trigger',
              pos: 'bottom-0 left-1/2 -translate-x-1/2',
              delay: 1
            }, {
              icon: Shield,
              label: 'Auth',
              pos: 'top-1/2 left-0 -translate-y-1/2',
              delay: 2
            }, {
              icon: Workflow,
              label: 'Process',
              pos: 'top-1/2 right-0 -translate-y-1/2',
              delay: 3
            }].map((item, i) => <motion.div key={i} animate={{
              y: [0, -5, 0],
              scale: [1, 1.05, 1]
            }} transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: item.delay
            }} className={`absolute ${item.pos} w-20 h-20 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-sky-50 flex flex-col items-center justify-center z-10`} data-id="element-146">
                  <item.icon size={20} className="text-sky-500 mb-1" data-id="element-147" />
                  <span className="text-[10px] font-medium text-slate-600" data-id="element-148">
                    {item.label}
                  </span>
                </motion.div>)}

              {/* Connecting Lines (SVG) */}
              <svg className="absolute inset-0 w-full h-full z-0 pointer-events-none" data-id="element-149">
                <motion.path d="M224 224 L224 40" stroke="#E0F2FE" strokeWidth="2" strokeDasharray="4 4" animate={{
                strokeDashoffset: [0, -8]
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-150" />
                <motion.path d="M224 224 L224 408" stroke="#E0F2FE" strokeWidth="2" strokeDasharray="4 4" animate={{
                strokeDashoffset: [0, 8]
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-151" />
                <motion.path d="M224 224 L40 224" stroke="#E0F2FE" strokeWidth="2" strokeDasharray="4 4" animate={{
                strokeDashoffset: [0, -8]
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-152" />
                <motion.path d="M224 224 L408 224" stroke="#E0F2FE" strokeWidth="2" strokeDasharray="4 4" animate={{
                strokeDashoffset: [0, 8]
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} data-id="element-153" />
              </svg>

              {/* Background Glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-sky-200/30 rounded-full blur-3xl -z-10" data-id="element-154"></div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}